import {
  AfterContentInit,
  Component,
  forwardRef,
  Inject,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  AG_GRID_LOCALE_EN,
  AG_GRID_LOCALE_IT,
  IoniconsOutline,
  READ_ONLY_INVALID_ACTIONS,
  WidgetValidator
} from '@pt/pt-models';
import { Subject } from 'rxjs';
import { ValidatorGenerator } from '../../utils/validator-generator';
import { ApiClientConfig, CapabilitiesService } from '@pt/api-client';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { DocumentPermissionDataUtils } from './document-permission-data-utils';
import { ListGroupAndUserResponse } from 'it/primatoo/openapi/api/org/schemas/capability/ListGroupAndUserResponse';
import { DocumentPermissionData } from './document-permission-data';
import { ag_grid_frameworkComponents } from '../../../pt-shared.constant';
import { AppLinkBuilder } from '../../../utils';

@Component({
  selector: 'pt-input-document-permission-selector',
  templateUrl: './input-document-permission-selector.component.html',
  styleUrls: ['input-document-permission-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputDocumentPermissionSelectorComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputDocumentPermissionSelectorComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class InputDocumentPermissionSelectorComponent implements OnInit, OnDestroy, AfterContentInit, ControlValueAccessor, OnChanges {
  @ViewChild('deleteCol', { static: true }) deleteColTemplateRef: TemplateRef<unknown>;
  @ViewChild('accessCol', { static: true }) accessColTemplateRef: TemplateRef<unknown>;
  @ViewChild('actionCol', { static: true }) actionColTemplateRef: TemplateRef<unknown>;
  @ViewChild('nameCol', { static: true }) nameColTemplateRef: TemplateRef<unknown>;
  @ViewChild('nameLockedCol', { static: true }) nameLockedColTemplateRef: TemplateRef<unknown>;

  @Input() isAdmin: boolean;
  @Input() ownerId: number;
  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label: string;

  @Input() accessValue: string[];
  @Input() actionValue: string[];
  @Input() placeholder?: string;

  public actionReadOnlyValue: { name: string, disabled: boolean }[];
  public actionWriteValue: { name: string, disabled: boolean }[];

  public lockedGridApi: GridApi;
  public lockedColumnApi: ColumnApi;
  public lockedGridOptions: GridOptions = null;
  public lockedTableData: DocumentPermissionData[];
  public lockedColumnsDef: ColDef[];
  public lockedIcon = IoniconsOutline.lock;
  public userIcon = IoniconsOutline.user;
  public ownerIcon = IoniconsOutline.shieldHalf;
  public formGroup: FormGroup;
  public isRequired: boolean;
  public inputId: string;
  public unselectedUsersOrGroups: DocumentPermissionData[] = [];
  public selectedUsersOrGroups: DocumentPermissionData[] = [];
  public gridApi: GridApi;
  public columnApi: ColumnApi;
  public tableData: DocumentPermissionData[];
  public gridOptions: GridOptions = null;
  public columnsDef: ColDef[];
  public IconRemove = IoniconsOutline.close;
  public isDisabled = false;
  public allGroupAndUsers: ListGroupAndUserResponse;
  private lockedHeaderName = 'Administrators';
  private headerName = 'Nome';
  private headerAccess = 'Accesso';
  private headerAction = 'Azioni';
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;
  private readonly env;

  constructor(@Inject(ApiClientConfig) private config,
              private permissionService: CapabilitiesService,
              private translate: TranslateService) {
    this.env = this.config.env;

    this.translate.stream('AG_GRID.HEADERS.administrators').subscribe((s) => {
      this.lockedHeaderName = s;
    });
    this.translate.stream('AG_GRID.HEADERS.name').subscribe((s) => {
      this.headerName = s;
    });
    this.translate.stream('AG_GRID.HEADERS.access').subscribe((s) => {
      this.headerAccess = s;
    });
    this.translate.stream('AG_GRID.HEADERS.actions').subscribe((s) => {
      this.headerAction = s;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.actionValue) {
      const ac: string[] = changes.actionValue.currentValue || [];
      const disabledReadOnlyActions: string[] =
        READ_ONLY_INVALID_ACTIONS;
      this.actionReadOnlyValue = ac.map((name) => {
        const disabled = disabledReadOnlyActions.includes(name);
        return {name, disabled}
      });

      this.actionWriteValue = ac.map((name) => {
        const disabled = false;
        return {name, disabled}
      });
    }
    }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.loadUsersAndGroups();

    this.lockedGridOptions = {
      ...this.env.defaultGridOptions,
      localeText: this.translate.currentLang == 'it' ? AG_GRID_LOCALE_IT : AG_GRID_LOCALE_EN,
      suppressPropertyNamesCheck: true,
      components: ag_grid_frameworkComponents,
    } as GridOptions;

    this.gridOptions = {
      ...this.env.defaultGridOptions,
      localeText: this.translate.currentLang == 'it' ? AG_GRID_LOCALE_IT : AG_GRID_LOCALE_EN,
      suppressPropertyNamesCheck: true,
      components: ag_grid_frameworkComponents,
    } as GridOptions;

    this.inputId = 'ptPermission_' + this.controlName;
    this.isRequired = ValidatorGenerator.validatorsContainsRequired(this.validators);
    //
    const validator = ValidatorGenerator.generateValidator(this.validators);
    this.formGroup = new FormGroup({
      value: new FormControl([], validator)
    });

    this.formGroup.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(t => {
      this.alignTableData();
      if (this.gridApi) {
        this.gridApi.redrawRows();
      }
      if (this.onChanged) {
        this.onChanged(t.value);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    });
  }

  writeValue(data: DocumentPermissionData[]): void {
    this.formGroup.controls.value.setValue(data, {emitEvent: false});
    this.alignTableData();
    if (this.gridApi) {
      this.gridApi.redrawRows();
    }
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  validate() {
    const actualValue: DocumentPermissionData[] = this.formGroup.controls.value.value || [];
    return (actualValue.length > 0 && actualValue.find(a => !a.access)) && {invalid: true};
  }

  removeGroupOrUser(item: DocumentPermissionData) {
    const currentValue: DocumentPermissionData[] = this.formGroup.controls.value.value;
    if (currentValue) {
      this.formGroup.controls.value.setValue(JSON.parse(JSON.stringify(this.tableData.filter(c => !(c.id === item.id && c.type === item.type)))), {emitEvent: true});
      this.alignTableData();

      this.alignSelector();
    }
  }

  onLockedGridReady(params) {
    this.lockedGridApi = params.api;
    this.lockedColumnApi = params.columnApi;
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
  }

  loadUsersAndGroups() {
    this.permissionService.findUserAndUsersGroups().subscribe((response: ListGroupAndUserResponse) => {
      this.allGroupAndUsers = response;
      this.alignSelector();
    }, () => {
      this.allGroupAndUsers = {users: [], userGroups: []};
      this.alignSelector();
    })
  }

  addNewUserOrUserGroup() {
    if (this.selectedUsersOrGroups.length > 0) {
      const currentValue: DocumentPermissionData[] = this.formGroup.controls.value.value;
      currentValue.push(...this.selectedUsersOrGroups);
      this.formGroup.controls.value.setValue(currentValue);
      this.alignSelector();
    }
  }

  customSearchFn(term: string, item: any) {
    term = term.toLowerCase();
    return item.name.toLowerCase().indexOf(term) > -1;
  }

  ngAfterContentInit(): void {
    this.lockedColumnsDef = DocumentPermissionDataUtils.getColLockedDef(this.nameLockedColTemplateRef, this.lockedHeaderName);

    this.columnsDef = DocumentPermissionDataUtils.getColDef(this.nameColTemplateRef,
      this.actionColTemplateRef,
      this.accessColTemplateRef,
      this.deleteColTemplateRef,
      this.headerName,
      this.headerAccess + '*',
      this.headerAction);
  }

  changeTableRowAccess(row: DocumentPermissionData, $event: string) {
    this.tableData.forEach(t => {
      if (t.id === row.id && t.type === row.type) {
        t.access = $event
        if (t.access === 'NONE') {
          t.actions = [];
        }
      }
    })
  }

  closeTableRowAccess() {
    this.formGroup.controls.value.setValue(this.tableData);
  }

  removeTableRowAction(row: DocumentPermissionData, action: { name: string, disabled: boolean }) {
    this.tableData.map(t => {
      if (t.id === row.id && t.type === row.type) {
        t.actions = t.actions.filter((a) => a !== action.name)
      }
    })
    this.formGroup.controls.value.setValue(this.tableData);
  }

  clearRow(row: DocumentPermissionData) {
    this.tableData.forEach(t => {
      if (t.id === row.id && t.type === row.type) {
        t.actions = []
      }
    })
  }
  changeTableRowAction(row: DocumentPermissionData, $event: string[]) {
    this.tableData.forEach(t => {
      if (t.id === row.id && t.type === row.type) {
        t.actions = $event
      }
    })
  }

  closeTableRowAction() {
    this.formGroup.controls.value.setValue(this.tableData);
  }

  private alignTableData() {
    if (this.formGroup) {
      const data: DocumentPermissionData[] = JSON.parse(JSON.stringify(this.formGroup.controls.value.value))
      this.tableData = data.sort((a, b) => a.name > b.name ? 1 : -1);
    } else {
      this.tableData = [];
    }

  }

  private alignSelector() {
    const selectedUserOrGroup: DocumentPermissionData[] = this.formGroup.controls.value.value || [];
    const list: DocumentPermissionData[] = DocumentPermissionDataUtils.fromFindAllGroupAndUserResponse(this.allGroupAndUsers, this.ownerId);
    this.unselectedUsersOrGroups = list.filter((u) => {
      switch (true) {
        case u.owner:
          return false;
        case u.admin:
          return false;
        case !!selectedUserOrGroup.find(s => (s.id === u.id && s.type === u.type)):
          return false;
        default:
          return true;
      }
    });
    this.selectedUsersOrGroups = [];
    this.lockedTableData = list.filter((u) => u.owner || u.admin)
  }

  openGroup(row: DocumentPermissionData) {
    switch (row.type) {
      case "GROUP":
        window.open(AppLinkBuilder.linkOfEntity('userGroups', row.id), '_blank');
        break;
      case "USER":
        window.open(AppLinkBuilder.linkOfEntity('users', row.id), '_blank');
        break;
    }

  }
}
